import { $createCodeNode } from "@lexical/code-core";
import { $createHorizontalRuleNode } from "@lexical/extension";
import { $insertList } from "@lexical/list";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { $findMatchingParent, $insertNodeToNearestRoot } from "@lexical/utils";
import {
  $createParagraphNode,
  $getEditor,
  $getSelection,
  $isRangeSelection,
  type LexicalNode,
} from "lexical";
import type { CapabilityCommand } from "../core/capabilities";
import type { BlockType } from "../core/types";
import { PICK_ATTACHMENTS_COMMAND, type AttachmentOutcome } from "../features/attachments/commands";
import {
  DEFAULT_TABLE,
  INSERT_TABLE_COMMAND,
  type TableOutcome,
} from "../features/tables/commands";

const LIST_TYPES = { bullet: "bullet", number: "number", check: "check" } as const;

/** By type: the table nodes load on demand, and this file is part of every editor. */
const $isTableCell = (node: LexicalNode): boolean => node.getType() === "tablecell";

/** Whether the selection starts or ends in a table cell, where only paragraphs can stand. */
export function $isInTableCell(): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;
  return [selection.anchor, selection.focus].some(
    (point) => $findMatchingParent(point.getNode(), $isTableCell) !== null,
  );
}

/**
 * Converts the blocks the selection touches. Text and marks are kept; the block ID follows the
 * content (see `features/block-id`). Must run inside an update.
 */
export function $setBlock(block: BlockType): boolean {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;
  // Refused rather than converted and then turned back by the cell's own rule.
  if ($isInTableCell()) return false;
  if (block === "bullet" || block === "number" || block === "check") {
    $insertList(LIST_TYPES[block]);
    return true;
  }
  $setBlocksType(selection, () => {
    if (block === "paragraph") return $createParagraphNode();
    if (block === "quote") return $createQuoteNode();
    if (block === "code") return $createCodeNode();
    return $createHeadingNode(block);
  });
  return true;
}

/**
 * What a command from the capability registry does to the document. The registry says what exists
 * and what it is called; this is the only place that knows how. Must run inside an update.
 */
export function $runCommand(command: CapabilityCommand): boolean {
  if (command.action === "turn-into") return $setBlock(command.block);
  if (command.id === "table") {
    // Handled only where table support is loaded; the reason for a refusal is not needed here.
    const outcome: TableOutcome = { refused: null };
    const handled = $getEditor().dispatchCommand(INSERT_TABLE_COMMAND, {
      ...DEFAULT_TABLE,
      outcome,
    });
    return handled && outcome.refused === null;
  }
  if (command.id === "image" || command.id === "file") {
    // Opens the file dialog, from inside the key or click that chose the command. Handled only
    // where attachment support is loaded; what is picked goes in when the host's store has it.
    const outcome: AttachmentOutcome = { refused: null, accepted: 0 };
    const handled = $getEditor().dispatchCommand(PICK_ATTACHMENTS_COMMAND, {
      kind: command.id,
      outcome,
    });
    return handled && outcome.refused === null;
  }
  if (command.id === "divider") {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || $isInTableCell()) return false;
    const rule = $createHorizontalRuleNode();
    $insertNodeToNearestRoot(rule);
    return true;
  }
  return false;
}
